/**
 * Billing & Claims accordion section
 *
 * Wraps MemberBillingInfoForm with the standard autosave pattern. Selects and
 * tel inputs flush immediately on change; regular text fields debounce on
 * change and flush on blur. flushOnUnmount handles modal-close mid-edit.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { AutosaveStatus } from '@components/AutosaveStatus';
import { useAutoSave } from '@hooks/useAutoSave';
import { Box } from '@mui/material';
import type { ServiceFacilityResponse2 } from '@store/generated/billing';
import type { BillingEntityResponse } from '@store/generated/billing-entities';

import { MemberBillingInfoForm } from '../MemberBillingInfoForm';

import type { BillingFormData } from './usePersonCard';

interface BillingSectionProps {
  initialData: BillingFormData;
  onSave: (data: BillingFormData) => Promise<void>;
  disabled?: boolean;
  billingEntities?: BillingEntityResponse[];
  entitiesLoading?: boolean;
  serviceFacilities?: ServiceFacilityResponse2[];
  facilitiesLoading?: boolean;
}

// Fields that don't fire blur in a useful way (selects, tel inputs). Treat
// changes as commit-worthy so the save fires without waiting for blur.
const IMMEDIATE_FIELDS = new Set<keyof BillingFormData>([
  'billing_entity_id',
  'service_facility_id',
  'license_type',
  'tax_id_type',
  'phone',
  'fax_number',
]);

export function BillingSection({
  initialData,
  onSave,
  disabled = false,
  billingEntities,
  entitiesLoading,
  serviceFacilities,
  facilitiesLoading,
}: BillingSectionProps) {
  const [formData, setFormData] = useState<BillingFormData>(initialData);
  useEffect(() => {
    setFormData(initialData);
  }, [initialData]);

  const pendingDataRef = useRef<BillingFormData | null>(null);

  const performSave = useCallback(
    async (data: BillingFormData) => {
      await onSave(data);
    },
    [onSave],
  );

  const { triggerAutoSave, saveImmediately, status, lastSavedAt, hasPendingSave } = useAutoSave({
    onSave: performSave,
    debounceMs: 2000,
    flushOnUnmount: true,
  });

  const handleChange = useCallback(
    (field: keyof BillingFormData, value: string) => {
      // license_type comes through as ProviderLicenseType in the form, but the
      // form's onChange signature is `(field, string)` — no narrowing needed.
      const next = { ...formData, [field]: value };
      setFormData(next);
      pendingDataRef.current = next;

      if (IMMEDIATE_FIELDS.has(field)) {
        saveImmediately(next);
        pendingDataRef.current = null;
      } else {
        triggerAutoSave(next);
      }
    },
    [formData, saveImmediately, triggerAutoSave],
  );

  const handleBlur = useCallback(() => {
    if (pendingDataRef.current) {
      saveImmediately(pendingDataRef.current);
      pendingDataRef.current = null;
    }
  }, [saveImmediately]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <AutosaveStatus
          status={status}
          isDirty={hasPendingSave()}
          lastSavedAt={lastSavedAt}
          verbose
        />
      </Box>
      <Box onBlur={handleBlur}>
        <MemberBillingInfoForm
          // license_type is typed as ProviderLicenseType in the form, but the
          // onChange signature is `(field, string)` — same string round-trips
          // without narrowing, so the loose form data shape is safe to forward.
          data={formData}
          onChange={handleChange}
          disabled={disabled}
          billingEntities={billingEntities}
          entitiesLoading={entitiesLoading}
          serviceFacilities={serviceFacilities}
          facilitiesLoading={facilitiesLoading}
        />
      </Box>
    </Box>
  );
}
